import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useAuth, useClerk, useSignIn } from "@clerk/react";
import { codigoDoErro, mensagemDoErro } from "@/lib/painel/clerk-erros";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Spinner } from "@/components/ui/spinner";
import { CartaoEntrada } from "@/components/entrada/CartaoEntrada";
import { BotaoGoogle } from "@/components/entrada/BotaoGoogle";
import { CampoSenha } from "@/components/entrada/CampoSenha";
import { SeparadorOu } from "@/components/entrada/SeparadorOu";
import { SessaoAtiva } from "@/components/entrada/SessaoAtiva";

/**
 * Entrar — tela nossa, hooks do Clerk por baixo.
 *
 * Nenhum componente visual do Clerk: o `<SignIn />` deles chegava em inglês,
 * com o cartão e a tipografia deles e "Secured by Clerk" no pé. Aqui é shadcn
 * com os tokens da marca, e o Clerk só autentica.
 */
export function FormEntrar() {
  const { signIn, fetchStatus } = useSignIn();
  const signInPronto = fetchStatus !== "fetching" && Boolean(signIn);
  const { isSignedIn } = useAuth();
  const { signOut } = useClerk();

  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [googleCarregando, setGoogleCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [codigo, setCodigo] = useState<string | null>(null);
  const [saindo, setSaindo] = useState(false);
  const [entrandoPorConvite, setEntrandoPorConvite] = useState(false);

  // Convite por e-mail: o ticket É a prova, então entra sem pedir senha.
  useEffect(() => {
    if (!signIn) return;
    const t = new URLSearchParams(window.location.search).get("__clerk_ticket");
    if (!t) return;
    setEntrandoPorConvite(true);
    void (async () => {
      try {
        const res = await signIn.ticket({ ticket: t });
        if (res.error) {
          setEntrandoPorConvite(false);
          setCodigo(codigoDoErro(res.error) ?? null);
          setErro(mensagemDoErro(res.error));
          return;
        }
        await signIn.finalize({
          navigate: async ({ decorateUrl }) => {
            window.location.href = decorateUrl("/conversas");
          },
        });
      } catch (err) {
        setEntrandoPorConvite(false);
        setCodigo(codigoDoErro(err) ?? null);
        setErro(mensagemDoErro(err));
      }
    })();
  }, [signInPronto, signIn]);

  // O Clerk devolve o motivo na URL quando o login social falha (cancelar no Google).
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const cod = p.get("erro") ?? p.get("err_code") ?? p.get("error");
    if (!cod) return;
    setErro(mensagemDoErro({ errors: [{ code: cod }] }));
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  /** `session_exists` precisa de botão, não de frase — daí guardar o código. */
  function falhou(err: unknown) {
    const cod = codigoDoErro(err);
    setCodigo(cod ?? null);
    setErro(mensagemDoErro(err));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!signIn || ocupado) return;
    setOcupado(true);
    setErro(null);
    setCodigo(null);
    try {
      const res = await signIn.password({ identifier: email.trim().toLowerCase(), password: senha });
      if (res.error) {
        falhou(res.error);
        setOcupado(false);
        return;
      }
      const fin = await signIn.finalize({
        navigate: async ({ decorateUrl }) => {
          window.location.href = decorateUrl("/conversas");
        },
      });
      if (fin.error) {
        falhou(fin.error);
        setOcupado(false);
      }
    } catch (err) {
      falhou(err);
      setOcupado(false);
    }
  }

  async function loginComGoogle() {
    if (!signIn || googleCarregando) return;
    setGoogleCarregando(true);
    setErro(null);
    const desistir = window.setTimeout(() => {
      setGoogleCarregando(false);
      setErro("O Google não respondeu a tempo. Tente novamente ou acesse por e-mail e senha.");
    }, 12000);
    try {
      const res = await signIn.sso({
        strategy: "oauth_google",
        redirectCallbackUrl: `${window.location.origin}/sign-in/sso-callback`,
        redirectUrl: `${window.location.origin}/conversas`,
      });
      if (res.error) {
        window.clearTimeout(desistir);
        falhou(res.error);
        setGoogleCarregando(false);
      }
    } catch (err) {
      window.clearTimeout(desistir);
      falhou(err);
      setGoogleCarregando(false);
    }
  }

  if (entrandoPorConvite && !erro) {
    return (
      <CartaoEntrada rotulo="Convite" titulo="Entrando pelo convite…" descricao="Conectando você ao atendimento da clínica.">
        <Spinner className="text-primary" />
      </CartaoEntrada>
    );
  }

  if (isSignedIn) return <SessaoAtiva modo="entrar" />;

  const sessaoPresa = codigo === "session_exists" || codigo === "identifier_already_signed_in";

  return (
    <CartaoEntrada rotulo="Acesso" titulo="Entrar" descricao="Aqui ficam as conversas dos seus pacientes.">
      <BotaoGoogle rotulo="Continuar com o Google" carregando={googleCarregando} disabled={ocupado} onClick={() => void loginComGoogle()} />
      <SeparadorOu />

      <form onSubmit={onSubmit}>
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="email">E-mail</FieldLabel>
            <Input
              id="email"
              type="email"
              required
              autoComplete="email"
              placeholder="voce@clinica.com"
              className="h-11"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={ocupado}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="senha">Senha</FieldLabel>
            <CampoSenha
              id="senha"
              required
              autoComplete="current-password"
              placeholder="••••••••"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              disabled={ocupado}
            />
          </Field>

          {erro ? (
            <Alert variant="destructive">
              <AlertDescription>
                {erro}
                {sessaoPresa ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2 w-full"
                    disabled={saindo}
                    onClick={() => {
                      setSaindo(true);
                      void signOut({ redirectUrl: "/sign-in" });
                    }}
                  >
                    {saindo ? <Spinner /> : null}
                    Sair da conta atual e entrar com esta
                  </Button>
                ) : null}
              </AlertDescription>
            </Alert>
          ) : null}

          <Button type="submit" size="lg" className="w-full" disabled={ocupado || !email.trim() || !senha}>
            {ocupado ? <Spinner /> : null}
            {ocupado ? "Entrando…" : "Entrar no painel"}
          </Button>
        </FieldGroup>
      </form>

      <p className="mt-5 text-center text-xs text-muted-foreground">
        Primeiro acesso da sua clínica?{" "}
        <Link to="/sign-up" className="font-semibold text-primary hover:underline">
          Criar conta
        </Link>
      </p>

      <div id="clerk-captcha" />
    </CartaoEntrada>
  );
}
